/**
 * The installed app's icons, made in the browser from one uploaded logo.
 * Request of 5 October 2026.
 *
 * Four PNGs, the sizes the manifest and iOS ask for:
 * - 192 and 512 for "any": the logo fitted with a little room, the rest
 *   transparent, as a launcher that draws its own backdrop wants it;
 * - 512 "maskable": the background colour edge to edge and the logo inside the
 *   safe zone, the central circle of 80% that every Android mask keeps, so no
 *   launcher's circle, squircle or teardrop cuts into it;
 * - 180 for the iPhone home screen: the background colour filled in, because
 *   iOS shows a transparent pixel as black.
 *
 * A maskable icon the lab designed itself can be given instead; it is used
 * edge to edge, as such an icon is drawn to be.
 */

export interface AppIconSet {
  icon192: Blob;
  icon512: Blob;
  maskable512: Blob;
  appleTouch180: Blob;
}

/**
 * The largest square of the safe zone's circle (radius 40% of the icon) is
 * 0.4 * 2 / sqrt(2) = 56.6% of the side; a logo fitted inside that box is
 * inside the circle whatever its shape.
 */
export const MASKABLE_SAFE_FRACTION = 0.56;
export const ANY_FRACTION = 0.88;
export const APPLE_FRACTION = 0.8;

export interface Placement {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where a picture of `sourceWidth` by `sourceHeight` goes, fitted without
 * cropping or stretching inside a centred box of `fraction` of a `size` square.
 */
export function fitInside(
  sourceWidth: number,
  sourceHeight: number,
  size: number,
  fraction: number,
): Placement {
  const box = size * fraction;
  const scale = Math.min(box / Math.max(1, sourceWidth), box / Math.max(1, sourceHeight));
  const width = Math.round(sourceWidth * scale);
  const height = Math.round(sourceHeight * scale);
  return { x: Math.round((size - width) / 2), y: Math.round((size - height) / 2), width, height };
}

/** Whether a colour is "#rrggbb", the form the API takes. */
export function isHexColor(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

function load(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as a picture.'));
    };
    image.src = url;
  });
}

function draw(
  image: HTMLImageElement,
  size: number,
  fraction: number,
  background: string | null,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (context === null) return Promise.reject(new Error('This browser cannot draw the icons.'));
  if (background !== null) {
    context.fillStyle = background;
    context.fillRect(0, 0, size, size);
  }
  context.imageSmoothingQuality = 'high';
  const place = fitInside(
    image.naturalWidth || image.width,
    image.naturalHeight || image.height,
    size,
    fraction,
  );
  context.drawImage(image, place.x, place.y, place.width, place.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob === null ? reject(new Error('The icon could not be made.')) : resolve(blob)),
      'image/png',
    ),
  );
}

/** The four icons from a logo, and optionally a maskable icon of the lab's own design. */
export async function makeAppIcons(
  logo: Blob,
  background: string,
  maskable?: Blob,
): Promise<AppIconSet> {
  const image = await load(logo);
  const masked = maskable === undefined ? null : await load(maskable);
  return {
    icon192: await draw(image, 192, ANY_FRACTION, null),
    icon512: await draw(image, 512, ANY_FRACTION, null),
    maskable512:
      masked === null
        ? await draw(image, 512, MASKABLE_SAFE_FRACTION, background)
        : await draw(masked, 512, 1, background),
    appleTouch180: await draw(image, 180, APPLE_FRACTION, background),
  };
}
